<template>
  <div class="d-btn-toggle" :style="cssVars">
    <button
      v-for="btn in buttons"
      :key="btn.value"
      class="d-btn-toggle__button"
      :class="{
        first: btn === buttons[0],
        last: btn === buttons[buttons.length - 1],
        active: btn.value === localValue,
      }"
      @click="emit('update:modelValue', btn.value)"
    >
      <component
        :is="btn.iconComponent"
        v-if="btn.iconComponent && btn.icon"
        :icon="btn.icon"
        size="16px"
        :height="16"
        :width="16"
        class="d-btn-toggle__button-icon"
        style="margin-bottom: 1px"
      ></component>
      <span class="d-btn-toggle__button-text">{{ btn.label }}</span>
      <slot name="after" :button="btn"></slot>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { Colors } from '@/api';

interface Props {
  buttons: Array<{
    label: string;
    value: string;
    icon?: string;
    iconComponent?: string;
  }>;
  modelValue: string;
  primaryColor?: Colors;
}
interface Emits {
  (e: 'update:modelValue', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  primaryColor: Colors.BLUE,
});
const emit = defineEmits<Emits>();

const localValue = ref(props.modelValue);
watch(
  () => props.modelValue,
  (value) => {
    localValue.value = value;
  }
);

const cssVars = computed(() => {
  // Use CSS variables directly - no need for expensive useThemedColors()
  return {
    '--primary-color': `rgb(var(--tw-color-${props.primaryColor}))`,
  };
});
</script>

<style lang="scss" scoped>
.d-btn-toggle {
  @apply tw-flex tw-items-center;

  &__button {
    @apply tw-flex tw-items-center tw-text-gray-medium-dark tw-bg-white tw-px-3 tw-text-xs tw-border-t tw-border-b tw-border-l tw-border-gray-medium tw-relative;
    height: 2rem;

    &-icon {
      @apply tw-text-gray-medium-dark;
    }

    &-text {
      @apply tw-font-semibold tw-ml-1;
    }

    &:hover,
    &:hover &-icon,
    &.active,
    &.active &-icon {
      @apply tw-text-white;
      background-color: var(--primary-color);
    }

    &.first {
      @apply tw-rounded-tl tw-rounded-bl;
    }

    &.last {
      @apply tw-rounded-tr tw-rounded-br tw-border-r;
    }
  }
}
</style>
